<template>
  <div class="pagination-wrapper" v-if="pagination && pagination.last_page > 0">
    <div class="pagination-container">
      <!-- دکمه اولین صفحه -->
      <BaseButton
        title=""
        hint="اولین صفحه"
        variant="ghost"
        shape="circle"
        type="button"
        class="pagination-btn first-page-btn"
        :disabled="pagination.current_page <= 1"
        @click="changePage(1)"
        aria-label="اولین صفحه"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z" />
        </svg>
      </BaseButton>

      <!-- دکمه صفحه قبلی -->
      <BaseButton
        title=""
        hint="صفحه قبلی"
        variant="ghost"
        shape="circle"
        type="button"
        class="pagination-btn prev-page-btn"
        :disabled="pagination.current_page <= 1"
        @click="changePage(pagination.current_page - 1)"
        aria-label="صفحه قبلی"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
        </svg>
      </BaseButton>

      <!-- نمایش شماره صفحات -->
      <div class="page-numbers">
        <template v-for="page in visiblePages" :key="page">
          <BaseButton
            v-if="page !== '...'"
            :title="String(toPersianDigits(page))"
            variant="ghost"
            type="button"
            class="pagination-btn page-number"
            :class="{ active: page === pagination.current_page }"
            @click="changePage(page)"
            :aria-label="`صفحه ${toPersianDigits(page)}`"
          />
          <span v-else class="dots">...</span>
        </template>
      </div>

      <!-- دکمه صفحه بعدی -->
      <BaseButton
        title=""
        hint="صفحه بعدی"
        variant="ghost"
        shape="circle"
        type="button"
        class="pagination-btn next-page-btn"
        :disabled="pagination.current_page >= pagination.last_page"
        @click="changePage(pagination.current_page + 1)"
        aria-label="صفحه بعدی"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
        </svg>
      </BaseButton>

      <!-- دکمه آخرین صفحه -->
      <BaseButton
        title=""
        hint="آخرین صفحه"
        variant="ghost"
        shape="circle"
        type="button"
        class="pagination-btn last-page-btn"
        :disabled="pagination.current_page >= pagination.last_page"
        @click="changePage(pagination.last_page)"
        aria-label="آخرین صفحه"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM18 6v12h-2V6h2z" />
        </svg>
      </BaseButton>
    </div>
    
    <!-- نمایش اطلاعات صفحه -->
    <div class="page-info">
      صفحه {{ toPersianDigits(pagination.current_page) }} از {{ toPersianDigits(pagination.last_page) }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { toPersianDigits } from '@/utils/formatters.js';
import BaseButton from '@/components/UiCompunents/BaseButton.vue'

/* eslint-disable vue/multi-word-component-names */
// eslint-disable-next-line no-undef
const props = defineProps({
  pagination: {
    type: Object,
    required: true,
    default: () => ({ last_page: 1, current_page: 1 }), // Add a default to prevent errors
  },
});

// eslint-disable-next-line no-undef
const emit = defineEmits(["page-change"]);

function changePage(page) {
  if (page > 0 && page <= props.pagination.last_page) {
    emit("page-change", page);
  }
}

// toPersianDigits imported from utils/formatters.js

// محاسبه صفحات قابل مشاهده
const visiblePages = computed(() => {
  const current = props.pagination.current_page;
  const last = props.pagination.last_page;
  const pages = [];

  if (last <= 7) {
    // اگر تعداد صفحات کم است، همه را نمایش بده
    for (let i = 1; i <= last; i++) {
      pages.push(i);
    }
  } else {
    // نمایش صفحات با نقطه چین
    if (current <= 3) {
      for (let i = 1; i <= 3; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(last);
    } else if (current >= last - 2) {
      pages.push(1);
      pages.push('...');
      for (let i = last - 2; i <= last; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = current - 1; i <= current + 1; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(last);
    }
  }

  return pages;
});
</script>

<style scoped>
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 16px 20px;
  background: var(--background-color);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(var(--text-color-secondary-rgb), 0.1);
  border: 1px solid var(--text-color-secondary);
  border-color: rgba(var(--text-color-secondary-rgb), 0.2);
  direction: rtl;
  transition: all 0.3s ease;
}

.pagination-container {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pagination-btn :deep(.base-button) {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 8px;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.3);
  background-color: var(--background-color);
  color: var(--text-color-secondary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  user-select: none;
  font-family: 'IRANSans', Arial, sans-serif;
}

.pagination-btn :deep(.base-button:hover:not(:disabled)) {
  background-color: var(--hover-background-color);
  border-color: var(--background-color-button);
  color: var(--background-color-button);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(var(--background-color-button-rgb), 0.15);
}

.pagination-btn :deep(.base-button:disabled) {
  cursor: not-allowed;
  opacity: 0.4;
  background-color: var(--background-color-secondary);
  color: var(--text-color-secondary);
  border-color: rgba(var(--text-color-secondary-rgb), 0.2);
}

.pagination-btn :deep(.base-button:disabled:hover) {
  transform: none;
  box-shadow: none;
  background-color: var(--background-color-secondary);
  border-color: rgba(var(--text-color-secondary-rgb), 0.2);
  color: var(--text-color-secondary);
}

.page-numbers {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 8px;
}

.page-number :deep(.base-button) {
  font-family: 'IRANSans', Arial, sans-serif;
}

.page-number.active :deep(.base-button) {
  background-color: var(--background-color-button);
  color: var(--text-color-forthly);
  border-color: var(--background-color-button);
  box-shadow: 0 2px 6px rgba(var(--background-color-button-rgb), 0.3);
}

.page-number.active :deep(.base-button:hover) {
  background-color: var(--background-color-button-hover);
  border-color: var(--background-color-button-hover);
  transform: none;
}

.dots {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  color: var(--text-color-secondary);
  font-weight: bold;
  user-select: none;
  font-family: 'IRANSans', Arial, sans-serif;
}

.first-page-btn :deep(.base-button),
.last-page-btn :deep(.base-button) {
  background-color: var(--background-color-secondary);
  border-color: rgba(var(--text-color-secondary-rgb), 0.3);
}

.first-page-btn :deep(.base-button:hover:not(:disabled)),
.last-page-btn :deep(.base-button:hover:not(:disabled)) {
  background-color: var(--hover-background-color);
  border-color: var(--text-color-secondary);
}

.prev-page-btn :deep(.base-button),
.next-page-btn :deep(.base-button) {
  background-color: var(--background-color-button2);
  border-color: rgba(var(--text-color-secondary-rgb), 0.3);
}

.prev-page-btn :deep(.base-button:hover:not(:disabled)),
.next-page-btn :deep(.base-button:hover:not(:disabled)) {
  background-color: var(--background-color-button-hover2);
  border-color: var(--text-color-secondary);
}

.prev-page-btn svg,
.next-page-btn svg {
  transform: rotate(180deg);
}

.first-page-btn svg,
.last-page-btn svg {
  transform: rotate(180deg);
}

.page-info {
  color: var(--text-color-secondary);
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  font-family: 'IRANSans', Arial, sans-serif;
}

/* Responsive Design */
@media (max-width: 768px) {
  .pagination-wrapper {
    flex-direction: column;
    gap: 12px;
    padding: 12px 16px;
    margin-top: 16px;
  }

  .pagination-container {
    order: 2;
  }

  .page-info {
    order: 1;
    font-size: calc(0.2vw + 10px);
  }

  .pagination-btn {
    min-width: 32px;
    height: 32px;
    font-size: calc(0.2vw + 10px);
  }

  .page-numbers {
    margin: 0 4px;
    gap: 2px;
  }

  .dots {
    min-width: 32px;
    height: 32px;
  }
}

@media (max-width: 480px) {
  .pagination-wrapper {
    padding: 8px 12px;
    margin-top: 12px;
    border-radius: 8px;
  }

  .pagination-btn {
    min-width: 28px;
    height: 28px;
    font-size: calc(0.2vw + 8px);
    border-radius: 6px;
  }

  .dots {
    min-width: 28px;
    height: 28px;
  }

  .page-info {
    font-size: calc(0.2vw + 8px);
  }

  .page-numbers {
    gap: 1px;
    margin: 0 2px;
  }
}

@media (max-width: 360px) {
  .pagination-wrapper {
    padding: 6px 8px;
    margin-top: 10px;
  }

  .pagination-btn {
    min-width: 24px;
    height: 24px;
    font-size: 0.7rem;
  }

  .dots {
    min-width: 24px;
    height: 24px;
  }

  .page-info {
    font-size: 0.7rem;
  }
}
</style>
